import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap/modal/modal-ref';

import { AccountService } from '../../../../../services/account/account.service';
import { PaymentService } from '../../../../../services/payment/payment.service';
import {
  StripeTerminalServerDrivenPaymentModalComponent,
} from '../stripe-terminal-server-driven-payment-modal/stripe-terminal-server-driven-payment-modal.component';
import { OrganizationConfigService } from '../../../../../services/organization-config/organization-config.service';
import { CustomerService } from '../../../../../services/customer/customer.service';
import { CustomerPaymentMethod, CustomerPaymentMethodType } from '../../../../../models/customer';
import { capitalizeFirstLetter, delay } from '../../../../../utils';
import { PaymentProcessorType } from '../../../../../models/organization-config-enums';
import { InvoiceStatus, PaymentStatus } from '../../../../../models/payment-enums';
import { getInvoiceStatusDisplay } from '../../../../../models/payment';
import * as liveReaderRefusal from '../live-reader-refusal';

declare const emergepay: any;

interface SavedCardOptions extends liveReaderRefusal.LiveReaderActionOptions {
  paymentMethod?: CustomerPaymentMethod;
}


@Component({
  selector: 'app-select-payment-method-modal',
  templateUrl: './select-payment-method-modal.component.html',
  styleUrls: ['./select-payment-method-modal.component.css']
})
export class SelectPaymentMethodModalComponent implements OnInit {
  @Input() invoiceId: number;
  @Input() paymentProcessorType: PaymentProcessorType;
  @Input() customerId: number;
  @Input() paymentDescription: string;
  @Input() amount?: number;

  // URLs redirected to when the payment succeeds or is cancelled, respectively.
  // Used for payment methods which require a redirect to an external site.
  @Input() successUrl: string;
  @Input() cancelUrl: string;

  // A function called when the payment succeeds.
  // Used for payment methods which do not require a redirect to an external site.
  @Input() onSuccess: () => void;

  // A function called when this modal (or, for the card-reader hand-off, the
  // terminal payment modal it opens) closes for any reason.
  @Input() onClose: () => void = null;

  // Set before handing off to the terminal payment modal, so initPayment's own
  // modalRef.result handler knows not to also fire onClose for this modal's close.
  public handedOffToReaderModal = false;

  public customerPaymentMethods: CustomerPaymentMethod[] = [];
  public useSavedSelected = false;
  public selectedPaymentMethod: CustomerPaymentMethod;

  public loadingInvoice = true;
  public loadingPaymentMethods = false;
  public loadingPayment = false;
  public errorMessage  = '';
  public liveReaderRefusal: liveReaderRefusal.LiveReaderRefusal = null;
  public cannotPayErrorMessage  = '';
  public serverError: number = null;
  public customerAvailableCredit: number | null = null;
  public creditAppliedMessage = '';

  public capitalizeFirstLetter = capitalizeFirstLetter;

  constructor(
    public activeModal: NgbActiveModal,
    public orgConfig: OrganizationConfigService,
    private accountService: AccountService,
    private paymentService: PaymentService,
    private customerService: CustomerService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    this.loadInvoiceThenPaymentMethods();
  }

  public hasCardReaderOption() {
    return [
      PaymentProcessorType.stripe,
      PaymentProcessorType.mxMerchant,
    ].includes(this.paymentProcessorType);
  }

  public handleManualCardEntry() {
    this.errorMessage = '';

    if (this.paymentProcessorType === PaymentProcessorType.stripe) {
      this.handleStripeCheckout();
    } else if (this.paymentProcessorType === PaymentProcessorType.gravity) {
      this.handleGravityModal();
    } else if (this.paymentProcessorType === PaymentProcessorType.mxMerchant) {
      this.handleMXMerchantPaymentLink();
    } else if (this.paymentProcessorType === PaymentProcessorType.mock) {
      this.handleMock();
    }
  }

  public handleCardReader() {
    this.errorMessage = '';

    this.handedOffToReaderModal = true;
    this.activeModal.close();

    let modalRef: NgbModalRef;
    modalRef = this.modalService.open(
      StripeTerminalServerDrivenPaymentModalComponent,
      {
        // Prevent the backdrop from closing the modal, in order to avoid issues
        // with payments in progress.
        // This is necessary because there's no way to stop the close from
        // within the modal component.
        backdrop: 'static',
        // Esc while a payment is being sent leaves no paymentId to cancel, stranding the reader push.
        keyboard: false
      }
    );

    modalRef.componentInstance.invoiceId = this.invoiceId;
    modalRef.componentInstance.customerId = this.customerId;
    modalRef.componentInstance.paymentDescription = this.paymentDescription;
    modalRef.componentInstance.amount = this.amount;
    modalRef.componentInstance.onSuccess = this.onSuccess;

    modalRef.result.then(() => this.onClose?.(), () => this.onClose?.());
  }

  public handleSavedCard(options: SavedCardOptions = {}): Promise<liveReaderRefusal.LiveReaderCheckOutcome> {
    // A recheck or bypass charges the card the refused request used, not the current selection.
    const paymentMethod = options.paymentMethod ?? this.selectedPaymentMethod;
    if (!paymentMethod) { return this.settleLiveReaderAction(Promise.resolve('other')); }

    this.errorMessage = '';
    this.startLiveReaderAction(options);

    this.loadingPayment = true;

    const outcome = this.paymentService
      .payInvoiceUsingSavedPaymentMethod(
        this.invoiceId,
        paymentMethod.id,
        this.amount,
        options.bypass
      )
      .then(paymentId => {
        return this.checkPayment(paymentId);
      })
      .then((): liveReaderRefusal.LiveReaderCheckOutcome => 'cleared')
      .catch((reason): liveReaderRefusal.LiveReaderCheckOutcome => {
        this.loadingPayment = false;
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to charge this saved payment method.';
        } else if (liveReaderRefusal.isLiveReaderUnconfirmedError(reason)) {
          return this.handleLiveReaderUnconfirmed(
            reason, options, opts => this.handleSavedCard({ ...opts, paymentMethod })
          );
        } else if (liveReaderRefusal.isLiveReaderCapturedError(reason)) {
          this.closeAsAlreadyPaid(liveReaderRefusal.getErrorMessageText(reason));
        } else if (reason.status === 400) {
          this.errorMessage = reason.error;
        } else {
          this.serverError = reason.status;
        }
        return 'other';
      })
      .finally(() => { this.loadingPayment = false; });
    return this.settleLiveReaderAction(outcome);
  }

  public canUseAccountCredit(): boolean {
    return (
      !this.loadingInvoice &&
      this.customerAvailableCredit !== null &&
      this.customerAvailableCredit > 0
    );
  }

  public accountCreditLabel(): string {
    if (this.loadingInvoice) { return 'Use Account Credit - Loading'; }
    return (
      'Use Account Credit (' +
      this.orgConfig.formatCurrency(this.customerAvailableCredit ?? 0) +
      ' available)'
    );
  }

  public handleAccountCredit(options: liveReaderRefusal.LiveReaderActionOptions = {}
                             ): Promise<liveReaderRefusal.LiveReaderCheckOutcome> {
    if (!this.canUseAccountCredit()) { return this.settleLiveReaderAction(Promise.resolve('other')); }

    this.errorMessage = '';
    this.creditAppliedMessage = '';
    this.startLiveReaderAction(options);

    this.loadingPayment = true;

    const outcome = this.paymentService
      .payInvoiceUsingAccountCredit(this.invoiceId, this.amount, options.bypass)
      .then((result): liveReaderRefusal.LiveReaderCheckOutcome => {
        if (result.invoice_status === InvoiceStatus.paymentComplete) {
          // Leave the loading message in place until the modal is closed.
          this.activeModal.close();
          this.onSuccess();
          return 'cleared';
        }

        const applied = Number(result.amount_applied);
        const remaining = Number(result.invoice_amount_due);

        if (this.amount && applied >= this.amount) {
          // The requested amount is paid in full; the invoice itself may still owe.
          this.activeModal.close();
          this.onSuccess();
          return 'cleared';
        }
        if (this.amount) {
          this.amount = Math.round((this.amount - applied) * 100) / 100;
        }

        this.creditAppliedMessage = (
          this.orgConfig.formatCurrency(applied) + ' of account credit applied. ' +
          this.orgConfig.formatCurrency(remaining) + ' remaining.'
        );
        this.loadingPayment = false;
        this.loadInvoiceThenPaymentMethods();
        return 'cleared';
      })
      .catch((reason): liveReaderRefusal.LiveReaderCheckOutcome => {
        this.loadingPayment = false;
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to apply account credit.';
        } else if (liveReaderRefusal.isLiveReaderUnconfirmedError(reason)) {
          return this.handleLiveReaderUnconfirmed(reason, options, opts => this.handleAccountCredit(opts));
        } else if (liveReaderRefusal.isLiveReaderCapturedError(reason)) {
          this.closeAsAlreadyPaid(liveReaderRefusal.getErrorMessageText(reason));
        } else if (reason.status === 400) {
          this.errorMessage = reason.error;
        } else {
          this.serverError = reason.status;
        }
        return 'other';
      });
    return this.settleLiveReaderAction(outcome);
  }

  private loadInvoiceThenPaymentMethods() {
    this.loadingInvoice = true;

    this.paymentService
      .getInvoice(this.invoiceId)
      .then(invoice => {
        this.loadingInvoice = false;

        if (![InvoiceStatus.paymentPending,
              InvoiceStatus.depositPaid].includes(invoice.status)) {
          this.cannotPayErrorMessage = (
            'This invoice cannot be paid because its status is ' +
            getInvoiceStatusDisplay(invoice.status) +
            '.'
          );
          return;
        }

        this.customerAvailableCredit = (
          invoice.customer_available_credit === null
            ? null
            : Number(invoice.customer_available_credit)
        );

        if (invoice.amount_due <= 0) {
          return this.paymentService
            .markInvoiceWithNoAmountDueAsPaid(
              this.invoiceId
            )
            .then(paymentId => {
              return this.checkPayment(paymentId);
            })
            .catch(reason => {
              if (reason.status === 403) {
                this.cannotPayErrorMessage = 'You do not have permission to complete this invoice payment.';
              } else if (reason.status === 400) {
                this.cannotPayErrorMessage = reason.error;
              } else {
                this.serverError = reason.status;
              }
            });
        }

        this.loadingPaymentMethods = true;

        return this.customerService
          .getCustomerSavedPaymentMethods(
            this.customerId,
            CustomerPaymentMethodType.card,
            invoice.payment_processor_connection_id
          )
          .then(cpms => {
            this.customerPaymentMethods = cpms;
            if (this.customerPaymentMethods.length > 0) {
              this.selectedPaymentMethod = cpms[0];
            }
          });
      })
      .catch()
      .finally(() => {
        this.loadingInvoice = false;
        this.loadingPaymentMethods = false;
      });
  }

  private handleStripeCheckout() {
    this.loadingPayment = true;

    this.paymentService
      .initStripePayment(
        this.invoiceId,
        this.customerId,
        this.paymentDescription,
        this.successUrl,
        this.cancelUrl,
        this.amount
      )
      .then(sessionUrl => {
        location.assign(sessionUrl);
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }

        // Only remove the "loading payment" on case of failure - for successful initializations,
        // we leave the loading message in place until the redirect completes.
        this.loadingPayment = false;
      });
  }

  private handleGravityModal() {
    this.loadingPayment = true;

    emergepay.init();

    this.paymentService
      .initGravityPayment(
        this.invoiceId,
        this.customerId,
        this.amount
      )
      .then(transactionToken => {
        this.activeModal.close();

        emergepay.open({
          // (required) Used to set up the modal
          transactionToken,
          // (optional) Callback function that gets called after a successful transaction
          onTransactionSuccess: (approvalData) => {
            this.onSuccess();
            emergepay.close();
          },
          // (optional) Callback function that gets called after a failure occurs during the transaction (such as a declined card)
          onTransactionFailure: (failureData) => {
            emergepay.close();
          },
          // (optional) Callback function that gets called after a user clicks the close button on the modal
          onTransactionCancel: () => {
            emergepay.close();
          }
        });
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }

        // Only remove the "loading payment" on case of failure - for successful initializations,
        // we leave the loading message in place until the modal is closed.
        this.loadingPayment = false;
      });
  }

  private handleMXMerchantPaymentLink(options: liveReaderRefusal.LiveReaderActionOptions = {}
                                      ): Promise<liveReaderRefusal.LiveReaderCheckOutcome> {
    this.startLiveReaderAction(options);
    this.loadingPayment = true;

    const outcome = this.paymentService
      .initMXMerchantPayment(
        this.invoiceId,
        this.customerId,
        this.paymentDescription,
        this.successUrl,
        this.cancelUrl,
        this.amount,
        options.bypass
      )
      .then((paymentUrl): liveReaderRefusal.LiveReaderCheckOutcome => {
        location.assign(paymentUrl);
        return 'cleared';
      })
      .catch((reason): liveReaderRefusal.LiveReaderCheckOutcome => {
        // Only remove the "loading payment" on case of failure - for successful initializations,
        // we leave the loading message in place until the redirect completes.
        this.loadingPayment = false;

        if (reason.status === 403) {
          this.accountService.logout();
        } else if (liveReaderRefusal.isLiveReaderUnconfirmedError(reason)) {
          return this.handleLiveReaderUnconfirmed(reason, options, opts => this.handleMXMerchantPaymentLink(opts));
        } else if (liveReaderRefusal.isLiveReaderCapturedError(reason)) {
          this.closeAsAlreadyPaid(liveReaderRefusal.getErrorMessageText(reason));
        } else if (reason.status === 400) {
          this.errorMessage = (
            liveReaderRefusal.getErrorMessageText(reason) || 'The payment could not be started.'
          );
        } else {
          this.serverError = reason.status;
        }
        return 'other';
      });
    return this.settleLiveReaderAction(outcome);
  }

  private startLiveReaderAction(options: liveReaderRefusal.LiveReaderActionOptions) {
    if (!options.recheck) { this.liveReaderRefusal = null; }
  }

  private settleLiveReaderAction(outcome: Promise<liveReaderRefusal.LiveReaderCheckOutcome>
                                 ): Promise<liveReaderRefusal.LiveReaderCheckOutcome> {
    return outcome.then(settled => {
      if (settled !== 'live') { this.liveReaderRefusal = null; }
      return settled;
    });
  }

  private handleLiveReaderUnconfirmed(
    reason: any,
    options: liveReaderRefusal.LiveReaderActionOptions,
    retry: (options: liveReaderRefusal.LiveReaderActionOptions) => Promise<liveReaderRefusal.LiveReaderCheckOutcome>
  ): liveReaderRefusal.LiveReaderCheckOutcome {
    if (options.recheck) { return 'live'; }

    const refusal: liveReaderRefusal.LiveReaderRefusal = {
      message: liveReaderRefusal.getErrorMessageText(reason),
      checkAgain: () => retry({ recheck: true }),
      checking: false,
    };
    liveReaderRefusal.openProceedAnywayModal(
      this.modalService,
      refusal,
      () => retry({ bypass: true }),
      () => { this.liveReaderRefusal = refusal; }
    );
    return 'other';
  }

  private closeAsAlreadyPaid(messageText: string) {
    liveReaderRefusal.openAlreadyPaidModal(this.modalService, messageText);
    this.activeModal.close();
    this.onSuccess();
  }

  private handleMock() {
    this.loadingPayment = true;

    this.paymentService
      .makeMockPayment(
        this.invoiceId,
        this.customerId,
        this.amount
      )
      .then(() => {
        this.activeModal.close();
        this.onSuccess();
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }

        // Only remove the "loading payment" on case of failure - for successful payments,
        // we leave the loading message in place until the modal is closed.
        this.loadingPayment = false;
      });
  }

  private checkPayment(paymentId: number) {
    return this.paymentService
      .getPayment(paymentId)
      .then(payment => {
        if (payment.status === PaymentStatus.paymentComplete) {
          this.activeModal.close();
          this.onSuccess();
        } else if (payment.status === PaymentStatus.paymentPending) {
          return delay(1000)
            .then(() => {
              return this.checkPayment(paymentId);
            });
        } else {
          this.errorMessage = 'Payment failed.';
        }
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errorMessage = reason.reason;
        } else {
          this.serverError = reason.status;
        }
      })
  }
}
